import { getGateDirectionFromProtocolKey, getProtocolSource } from 'features/stepByStepProtocol/protocols/config';
import { readProtocolFromS3 } from 'features/stepByStepProtocol/services/s3ProtocolReader';
import { PROTOCOL_DIRECTION_BY_GATE_DIRECTION, PROTOCOL_STATUS } from 'features/stepByStepProtocol/utils/protocol/constants';
import { ProtocolParseError, parseProtocolXml } from 'features/stepByStepProtocol/utils/protocol/parseProtocolXml';

const settled = (status, { definition = null, meta = null, error = null } = {}) => ({ status, definition, meta, error });

/**
 * Reads and validates the protocol for one `locationId:gateDirection` key. Never throws: every
 * outcome is a status, so the caller can store it as is.
 *
 * - no protocol configured for the key -> NOT_CONFIGURED, nothing is read
 * - the file cannot be read (no bucket for the environment, HTTP error, network, timeout) -> UNAVAILABLE
 * - the file is not a valid protocol, or it is written for the other gate direction -> INVALID
 */
export async function fetchProtocol(protocolKey) {
    const source = getProtocolSource(protocolKey);
    if (!source) return settled(PROTOCOL_STATUS.NOT_CONFIGURED);

    let read;
    try {
        read = await readProtocolFromS3(source);
    } catch (err) {
        return settled(PROTOCOL_STATUS.UNAVAILABLE, { error: err?.message || 'Protocol read failed' });
    }
    if (!read?.ok) return settled(PROTOCOL_STATUS.UNAVAILABLE, { meta: read?.meta ?? null, error: read?.reason || 'Protocol read failed' });

    let definition;
    try {
        definition = parseProtocolXml(read.text);
    } catch (err) {
        const error = err instanceof ProtocolParseError ? err.message : 'Protocol XML could not be parsed';
        return settled(PROTOCOL_STATUS.INVALID, { meta: read.meta, error });
    }

    const expectedDirection = PROTOCOL_DIRECTION_BY_GATE_DIRECTION[getGateDirectionFromProtocolKey(protocolKey)];
    if (!expectedDirection || definition.direction !== expectedDirection) {
        return settled(PROTOCOL_STATUS.INVALID, {
            meta: read.meta,
            error: `Protocol direction "${definition.direction}" does not match the gate direction "${expectedDirection ?? 'unknown'}"`,
        });
    }

    return settled(PROTOCOL_STATUS.AVAILABLE, {
        definition,
        meta: { ...read.meta, protocolId: definition.id, schemaVersion: definition.schemaVersion },
    });
}
